import { dispatch } from '../index'
import { API, graphqlOperation, Auth, Analytics } from 'aws-amplify'
import { includesAny } from '../../lib/arrayOperations'
import * as Sentry from '@sentry/browser'
import config from '../../configuration/config'

import {
    APP_TOGGLE_ADMIN_MODE,
    APP_SET_SEARCH,
    APP_UPDATE_ATTRIBUTE,
    HANDLE_CHANGE,
    SET_USER_ATTRIBUTES,
    SET_USER_DATA,
    SHOW_UPDATE_ATTRIBUTES,
    SET_TRAINING_RESULTS,
    UPDATE_WINDOW_DIMENSIONS,
    UPDATE_BUILDING_DATA,
    SHOW_BUILDING_PICKER,
    UPDATE_USER_DATA,
    SET_ADMIN_STATUS,
    APP_SHOW_ERROR_PAGE,
    APP_SET_PLAYLISTS,
    APP_SET_PROPERTY_PLAYLISTS,
    UPDATE_USER_ROLES,
    UPDATE_TRAINING_RESULT,
    SET_USER_OCCUPANCIES,
    SHOW_TERMS_OF_USE,
    SET_TERMS_ACCEPTED
} from '../actionTypes'

const listOccupancy = `query($sk: String, $pk: ID) {
    listOccupancy(sk: $sk, pk: $pk) {
        items {
            pk
            sk
            d1
            d2
            roles
            firstName
            lastName
            email
            workPhone {
                number
                extension
            }
            phone {
                number
                extension
            }
            tenant
            building
            property
        }
    }
}`

const operation = `query ($sk: String, $d2: ID) {
    getOccupancy(sk: $sk, d2: $d2) {
        ... on Occupant {
            pk
            sk
            d1
            d2
            haveLoggedIn
            firstName
            lastName
            roles
            primaryContact
            workPhone {
                number
                extension
            }
            phone {
                number
                extension
            }
            lastLogin
            metaData {
                createdAt
            }
            extraFields {
                key
                value
            }
            email
            version
            __typename
        }
        ... on EvacuationAssistance {
            pk
            sk
            d1
            assistanceNeeded
            email
            createdAt
            endDate
            floor
            firstName
            lastName
            phone {
                number
                extension
            }
            startDate
            suite
            version
            buddies
            __typename
        }
        ... on Building {
            pk
            d1
            name
            version
            settings {
                closedCaption
                requiredFloorWardens
            }
            createdAt
            address {
                city
                country
                name
                region
                state
                street
                zipCode
            }
            assemblyAreas {
                coordinates {
                  latitude
                  longitude
                }
                title
              }
            coordinates {
                latitude
                longitude
            }
            settings {
                dsal
                modules {
                    title
                    enabled
                    key
                }
                phoneNumbers {
                    name
                    number
                }
                dsal
                systemConfig {
                    suites
                    support
                    systemNotifications
                    termsOfUse
                    userFields
                    whiteLabels
                    scheme
                    passwordLinkNeverExpiration
                }
            }
            __typename
        }
        ... on Property {
            pk
            d1
            name
            createdAt
            version
            resources {
                local {
                    title
                    url
                }
                regional {
                    title
                    url
                }
                federal {
                    title
                    url
                }
            }
            settings {
                positions {
                    enabled
                    floorWarden
                    team
                }
                phoneNumbers {
                    name
                    number
                }
                multiBuilding
                suites
                modules {
                    title
                    enabled
                    key
                }
            }
            __typename
        }
        ... on Client {
            pk
            sk
            name
            logo
            color {
                r
                b
                g
            }
            __typename
        }
        ... on Tenant {
            pk
            sk
            d1
            name
            floors
            suites
            metaData { 
                createdAt
            }
            version
            __typename
        }
        ... on TrainingResult {
            certificateType
            completedAt
            createdAt
            d1
            d2
            firstName
            lastName
            pk
            quizzesCompleted
            sk
            title {
                language
                text
            }
            updatedAt
            userType
            version
            videosCompleted
            __typename
        }
    }
}`

const listTrainingResults = `
    query ListTrainingResults(
        $sk: String!,
        $d2: String,
        $limit: Int,
        $nextToken: String
    ) {
        listTrainingResults(
            sk: $sk,
            d2: $d2,
            limit: $limit,
            nextToken: $nextToken
        ) {
            nextToken
            items {
                certificateType
                completedAt
                createdAt
                d1
                d2
                firstName
                lastName
                pk
                quizzesCompleted
                sk
                title {
                    language
                    text
                }
                updatedAt
                userType
                version
                videosCompleted
            }
        }
    }
`

export const setSearch = (event) => dispatch({ value: event.target.value, type: APP_SET_SEARCH })
export const handleChange = (name) => (event) => dispatch({ name, event, type: HANDLE_CHANGE })
export const updateAttribute = (attr, val) => dispatch({ attr, val, type: APP_UPDATE_ATTRIBUTE })
export const updateBuildingData = (data) => dispatch({ data, type: UPDATE_BUILDING_DATA })

export const setTrainingResults = trainingResults => dispatch({ trainingResults, type: SET_TRAINING_RESULTS })
export const updateTrainingResult = newResult => dispatch({ newResult, type: UPDATE_TRAINING_RESULT })
export const updateWindowDimensions = (width, height) => dispatch({ width, height, type: UPDATE_WINDOW_DIMENSIONS })
export const showUserConfigurator = (show) => dispatch({ show: show, type: SHOW_UPDATE_ATTRIBUTES })
export const updateUser = (user) => dispatch({ data: user, type: UPDATE_USER_DATA })
export const setUserAttributes = (user) => dispatch({ user, data: user.attributes, type: SET_USER_ATTRIBUTES })
export const setPlaylists = (playlists) => dispatch({ playlists, type: APP_SET_PLAYLISTS })
export const setPropertyPlaylists = (playlists) => dispatch({ playlists, type: APP_SET_PROPERTY_PLAYLISTS })
export const updateUserRoles = (roles) => dispatch({ roles, type: UPDATE_USER_ROLES })

export const showBuildingPicker = (show) => dispatch({ show, type: SHOW_BUILDING_PICKER })
export const showErrorPage = (show) => dispatch({ show, type: APP_SHOW_ERROR_PAGE })

export const setTermsAccepted = (accepted) => dispatch({
    type: SET_TERMS_ACCEPTED,
    payload: accepted
})

export const showTermsOfUse = (show) => dispatch({
    type: SHOW_TERMS_OF_USE,
    payload: show
})

export const getUserData = () => {
    Auth.currentAuthenticatedUser().then((user) => {
        dispatch({ user, data: user.attributes, type: SET_USER_ATTRIBUTES })

        Sentry.configureScope((scope) => {
            scope.setUser({ email: user.attributes.email })
        })

        // getProgramData(user)

        API.graphql(graphqlOperation(listOccupancy, { pk: user.username, sk: 'OCCUPANCY' }))
            .then(res => {
                let occupancy = res.data.listOccupancy.items

                dispatch({
                    data: occupancy,
                    type: SET_USER_OCCUPANCIES
                })
                
                if (occupancy.length > 1) {
                    showBuildingPicker(true)
                } else {
                    getProgramData(occupancy[0], user)
                }
            })
    })
}


export const getProgramData = async (data, user) => {

    const response = await API.graphql(graphqlOperation(operation, {
        d2: data.d2
    }))


    if (response.errors) {
        console.log("getProgramData: ", response.errors)
        Sentry.captureException(response.errors[0].message)
        showErrorPage(true)
        return null
    }

    let _property, _client, _occupant, _tenant, _evac, _building
    let _trainingResults = []

    response.data.getOccupancy.forEach((d) => {
        switch (d.__typename) {
            case 'Property':
                _property = d
                break
            case 'Client':
                _client = d
                break
            case 'Occupant':
                _occupant = d
                break
            case 'Tenant':
                _tenant = d
                break
            case 'EvacuationAssistance':
                _evac = d
                break
            case 'Building':
                _building = d
                break
            case 'TrainingResult':
                _trainingResults.push(d)
                break
            default:
                break
        }
    })

    const floor = _occupant.d1.split('#')[5] || null
    const suite = _occupant.d1.split('#')[6] || null

    dispatch({
        data: {
            tenant: _tenant,
            building: _building,
            property: _property,
            client: _client,
            floor,
            suite,
            userId: user.username,
            ..._occupant
        }, type: SET_USER_DATA
    })

    checkIfAdmin(_occupant)
    setTrainingResults(_trainingResults)
}

const checkIfAdmin = (user) => {
    let roles = user.roles
    const isAdmin = includesAny(roles, ['SUPER_ADMIN', 'PROPERTY_ADMIN', 'BUILDING_ADMIN', 'TENANT_ADMIN'])
    if (isAdmin) dispatch({ admin: true, type: SET_ADMIN_STATUS })
    return isAdmin

}
